/**
 * Нумерация зубов. Каноническая — FDI (ISO 3950): данные всегда хранятся в ней,
 * а Universal и Palmer остаются лишь способами отображения.
 *
 * Так сделано потому, что смена системы нумерации в хранилище означала бы
 * миграцию всех клинических записей, а разные страны и разные врачи привыкли к
 * разному. Отображение переключается настройкой, данные не трогаются.
 */

export type Fdi = number & { readonly __brand: 'Fdi' };

const PERMANENT_QUADRANTS = [1, 2, 3, 4] as const;
const PRIMARY_QUADRANTS = [5, 6, 7, 8] as const;

export function isPermanent(code: number): boolean {
  const q = Math.floor(code / 10);
  const n = code % 10;
  return (PERMANENT_QUADRANTS as readonly number[]).includes(q) && n >= 1 && n <= 8;
}

export function isPrimary(code: number): boolean {
  const q = Math.floor(code / 10);
  const n = code % 10;
  return (PRIMARY_QUADRANTS as readonly number[]).includes(q) && n >= 1 && n <= 5;
}

export function fdi(code: number): Fdi {
  if (!isPermanent(code) && !isPrimary(code)) {
    throw new Error(`Некорректный номер зуба по FDI: ${String(code)}`);
  }
  return code as Fdi;
}

/** Квадрант: 1 — верх справа, далее по часовой стрелке. */
export function quadrant(tooth: Fdi): number {
  return Math.floor(tooth / 10);
}

export function isUpper(tooth: Fdi): boolean {
  return [1, 2, 5, 6].includes(quadrant(tooth));
}

export function isRight(tooth: Fdi): boolean {
  return [1, 4, 5, 8].includes(quadrant(tooth));
}

/** Все зубы взрослого прикуса в порядке отображения формулы слева направо. */
export const PERMANENT_ARCH: readonly Fdi[] = [
  ...[8, 7, 6, 5, 4, 3, 2, 1].map((n) => fdi(10 + n)),
  ...[1, 2, 3, 4, 5, 6, 7, 8].map((n) => fdi(20 + n)),
  ...[8, 7, 6, 5, 4, 3, 2, 1].map((n) => fdi(40 + n)),
  ...[1, 2, 3, 4, 5, 6, 7, 8].map((n) => fdi(30 + n)),
];

/** Молочный прикус в том же порядке: верхний ряд слева направо, затем нижний. */
export const PRIMARY_ARCH: readonly Fdi[] = [
  ...[5, 4, 3, 2, 1].map((n) => fdi(50 + n)),
  ...[1, 2, 3, 4, 5].map((n) => fdi(60 + n)),
  ...[5, 4, 3, 2, 1].map((n) => fdi(80 + n)),
  ...[1, 2, 3, 4, 5].map((n) => fdi(70 + n)),
];

/** Поверхности живут в `dental`: они относятся к записи врача, а не к номеру. */
